// src/pages/Contact.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Mail, MapPinned, MessageCircle, ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { track } from '../utils/track';
import { Section } from '../styles';

// ---- Tokens --------------------------------------------------------------
const PURPLE = '#6C5CE7';
const PURPLE_LIGHT = '#EEECFC';
const INK = '#14162B';
const MUTED = '#6b7280';
const LINE = '#e5e7eb';
const CARD_BG = '#F8F8FB';
const DANGER = '#b3413c';

// ---- Layout ---------------------------------------------------------------
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1180px;
  margin: 0 auto;
  align-items: start;
  padding-top: 5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    padding-top: 3rem;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 0.4rem 0.9rem;
  color: ${INK};
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.75rem;
`;

const Headline = styled.h1`
  font-size: 3rem;
  line-height: 1.1;
  font-weight: 800;
  color: ${INK};
  margin: 0 0 1.25rem;

  @media (max-width: 900px) {
    font-size: 2.25rem;
  }
`;

const Subtext = styled.p`
  font-size: 1.1rem;
  color: ${MUTED};
  max-width: 30rem;
  margin: 0 0 2.5rem;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  background: ${PURPLE_LIGHT};
  color: ${PURPLE};
  flex-shrink: 0;
`;

const InfoLabel = styled.div`
  color: ${MUTED};
  font-size: 0.95rem;
`;

const InfoValue = styled.div`
  color: ${INK};
  font-weight: 700;
  font-size: 1.05rem;
`;

// ---- Card / form ------------------------------------------------------
const Card = styled.div`
  background: ${CARD_BG};
  border: 1px solid ${LINE};
  border-radius: 24px;
  padding: 2.5rem;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 12px;
  padding: 0.3rem;
  margin-bottom: 2rem;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 0.65rem 1rem;
  border-radius: 9px;
  border: none;
  background: ${({ $active }) => ($active ? PURPLE : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : MUTED)};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  /* USL CONTACT TABBUTTON COLOURS */
  background: ${({ $active }) => $active ? '#00A8E8' : '#F1F5F9'};
  color: ${({ $active }) => $active ? '#FFFFFF' : '#10243D'};
  border-color: ${({ $active }) => $active ? '#00A8E8' : '#CBD5E1'};

  &:hover {
    background: ${({ $active }) => $active ? '#0096D1' : '#E2E8F0'};
    color: ${({ $active }) => $active ? '#FFFFFF' : '#10243D'};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-weight: 600;
  color: ${INK};
  font-size: 0.9rem;

  input,
  select,
  textarea {
    font-weight: 400;
    font-size: 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${LINE};
    border-radius: 10px;
    font-family: inherit;
    background: #fff;

    &:focus {
      outline: none;
      border-color: ${PURPLE};
      box-shadow: 0 0 0 3px ${PURPLE_LIGHT};
    }
  }

  textarea {
    resize: vertical;
  }
`;

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 1.5rem;
  font-weight: 400;
  font-size: 0.95rem;
  color: ${INK};
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  padding: 0.85rem 2.2rem;
  border: none;
  border-radius: 10px;
  background: ${PURPLE};
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* USL CONTACT SUBMITBUTTON COLOURS */
  background: #00A8E8;
  color: #FFFFFF;
  border-color: #00A8E8;

  &:hover:not(:disabled) {
    background: #0096D1;
    border-color: #0096D1;
    color: #FFFFFF;
  }
`;

const ErrorText = styled.p`
  color: ${DANGER};
  font-size: 0.9rem;
`;

const SuccessText = styled.p`
  color: #1e8a4c;
  font-size: 0.9rem;
`;

// ---- Custom multiselect dropdown ---------------------------------------
const DropdownWrap = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${LINE};
  border-radius: 10px;
  background: #fff;
  font-size: 1rem;
  font-weight: 400;
  color: ${({ $hasValue }) => ($hasValue ? INK : '#9ca3af')};
  cursor: pointer;
  text-align: left;

  &:focus {
    outline: none;
    border-color: ${PURPLE};
    box-shadow: 0 0 0 3px ${PURPLE_LIGHT};
  }
`;

const DropdownPanel = styled.div`
  position: absolute;
  z-index: 10;
  top: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20, 22, 43, 0.12);
  max-height: 16rem;
  overflow-y: auto;
  padding: 0.4rem;
`;

const DropdownOption = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.7rem;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${INK};

  input {
    accent-color: ${PURPLE};
    width: 1.05rem;
    height: 1.05rem;
    flex-shrink: 0;
  }

  &:hover {
    background: ${PURPLE_LIGHT};
  }
`;

const ITEM_OPTIONS = [
  'Sofa(s)',
  'Wardrobe(s)',
  'Bed(s) and/or mattress(es)',
  'Large appliance (e.g. fridge, washing machine)',
  'TV',
  'Boxes',
  'Other',
];

const ItemsDropdown = ({ selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const summary = selected.length === 0
    ? 'Select items'
    : selected.length === 1
      ? selected[0]
      : `${selected[0]} +${selected.length - 1} more`;

  return (
    <DropdownWrap ref={ref}>
      <DropdownTrigger
        type="button"
        $hasValue={selected.length > 0}
        onClick={() => setOpen((o) => !o)}
      >
        {summary}
        <ChevronDown size={16} />
      </DropdownTrigger>

      {open && (
        <DropdownPanel>
          {ITEM_OPTIONS.map((item) => (
            <DropdownOption key={item}>
              {item}
              <input
                type="checkbox"
                checked={selected.includes(item)}
                onChange={() => onToggle(item)}
              />
            </DropdownOption>
          ))}
        </DropdownPanel>
      )}
    </DropdownWrap>
  );
};

// -------------------------------------------------------------------------

const TAB_CONTENT = {
  general: { label: 'General Enquiry' },
  van: { label: 'Man with a Van' },
};

const initialForm = {
  name: '',
  email: '',
  phone: '',
  message: '',
  items: [],
  timing: 'On a specific date',
  serviceDate: '',
  noDate: false,
  helpAvailable: 'Yes',
  duration: 'Half Day',
  pickupTown: '',
  destinationTown: '',
};

const Contact = () => {
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'van'
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleItem = (item) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.includes(item)
        ? prev.items.filter((i) => i !== item)
        : [...prev.items, item],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');

    let combinedMessage = form.message;

    if (activeTab === 'van') {
      combinedMessage = [
        `Pickup location: ${form.pickupTown}`,
        `Drop-off location: ${form.destinationTown}`,
        `Move date: ${
          form.noDate
            ? 'Not decided yet'
            : form.serviceDate || 'Not provided'
        }`,
        `What needs moving: ${form.message}`
      ].join('\\n');
    }

    const templateParams = {
      enquiryType: TAB_CONTENT[activeTab].label,
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: combinedMessage,
      time: new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        track('generate_lead', { form_name: `Contact - ${TAB_CONTENT[activeTab].label}` });
        setStatus('sent');
        setForm(initialForm);
      })
      .catch(() => setStatus('error'));
  };

  return (
    <Section>
      <Grid>
        {/* Left column — branding + contact info */}
        <div>
          <Badge><Mail size={16} /> Contact</Badge>
          <Headline>Need a hand with your move?</Headline>
          <Subtext>
            Questions about a delivery, or need a man with a van? We're just a message or call away.
          </Subtext>

          <InfoList>
            <InfoRow>
              <IconBadge><Mail size={20} /></IconBadge>
              <div>
                <InfoLabel>Email:</InfoLabel>
                <InfoValue>info@urgentservicelogistics.co.uk</InfoValue>
              </div>
            </InfoRow>
            <InfoRow>
              <IconBadge><MessageCircle size={20} /></IconBadge>
              <div>
                <InfoLabel>WhatsApp:</InfoLabel>
                <InfoValue>07436117842</InfoValue>
              </div>
            </InfoRow>
            <InfoRow>
              <IconBadge><MapPinned size={20} /></IconBadge>
              <div>
                <InfoLabel>Location:</InfoLabel>
                <InfoValue>Hampshire, UK</InfoValue>
              </div>
            </InfoRow>
          </InfoList>
        </div>

        {/* Right column — form card */}
        <Card>
          <TabRow>
            {Object.entries(TAB_CONTENT).map(([key, { label }]) => (
              <TabButton
                key={key}
                type="button"
                $active={activeTab === key}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </TabButton>
            ))}
          </TabRow>

          <StyledForm onSubmit={handleSubmit}>

              {activeTab === 'van' && (
                <>
                  <Row>
                    <Field>
                      Pickup location*
                      <input
                        type="text"
                        name="pickupTown"
                        placeholder="e.g. SO16 4AA"
                        value={form.pickupTown}
                        onChange={handleChange}
                        required
                      />
                    </Field>

                    <Field>
                      Drop-off location*
                      <input
                        type="text"
                        name="destinationTown"
                        placeholder="e.g. PO1 2AB"
                        value={form.destinationTown}
                        onChange={handleChange}
                        required
                      />
                    </Field>
                  </Row>

                  <Row>
                    <Field>
                      Move date
                      <input
                        type="date"
                        name="serviceDate"
                        value={form.serviceDate}
                        onChange={handleChange}
                        disabled={form.noDate}
                        style={{
                          opacity: form.noDate ? 0.5 : 1,
                          backgroundColor: form.noDate
                            ? '#f1f5f9'
                            : '#fff'
                        }}
                      />
                    </Field>

                    <Field as="div">
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginTop: '2rem',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={form.noDate}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setForm(prev => ({
                              ...prev,
                              noDate: checked,
                              serviceDate: checked
                                ? ''
                                : prev.serviceDate
                            }));
                          }}
                          style={{
                            width: '18px',
                            height: '18px',
                            flexShrink: 0
                          }}
                        />
                        I don't have a move date yet
                      </label>
                    </Field>
                  </Row>
                </>
              )}

              <Row>
                <Field>
                  First name*
                  <input type="text" name="name" value={form.name} onChange={handleChange} required />
                </Field>
                <Field>
                  {activeTab === 'van' ? 'Phone number*' : 'Phone number'}
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required={activeTab === 'van'}
                  />
                </Field>
              </Row>

              <Field>
                Email*
                <input type="email" name="email" value={form.email} onChange={handleChange} required />
              </Field>

              {activeTab === 'van' ? (
                <Field>
                  What needs moving?*
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="e.g. 2-seater sofa, double bed and 6 boxes"
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </Field>
              ) : (
                <Field>
                  Message*
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="Enter a question, feedback, or suggestions..."
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </Field>
              )}

              <SubmitButton type="submit" disabled={status === 'sending'}>
                {status === 'sending'
                  ? 'Sending…'
                  : activeTab === 'van'
                    ? 'Send quote request'
                    : 'Submit'}
              </SubmitButton>

              {status === 'error' && <ErrorText>Something went wrong — please try again.</ErrorText>}
              {status === 'sent' && <SuccessText>Thanks — we've got your enquiry and will be in touch shortly.</SuccessText>}
          </StyledForm>
        </Card>
      </Grid>
    </Section>
  );
};

export default Contact;